import { fromJS, List, Map } from 'immutable';

import { getActualDate, isAfter } from '../utils/time';
import { addThousandsSeparator } from '../utils/number';
import { goBack, navigateTo } from '../utils/navigation';
import api from '../utils/api';

import { ADMIN as INITIAL_STATE } from '../constants/store';
import { EMPTY, SEPARATOR } from '../constants';
import { ROUTES } from '../constants/routes';

import actions from '../actions';

const DEFAULT_LAST_SYNC_TIMESTAMP = '1998-12-31T23:00:00.000Z';

function getErrorString(error) {
  const errorStrings = [error?.toString()];

  if (error?.response?.text?.length > 0) {
    try {
      const responseBody = JSON.parse(error.response.text);

      if (responseBody && responseBody.error) {
        errorStrings.push(responseBody.error);
      }
    } catch (e) {
      errorStrings.push('Response body could not be parsed');
    }
  }

  return errorStrings.join(SEPARATOR.NEW_LINE);
}

function* setUnauthorized(reduction) {
  yield function effect() {
    navigateTo(ROUTES.ROOT);
  };

  return reduction.set('admin', EMPTY.MAP);
}

function* getUserInfo(reduction) {
  yield function effect(dispatch) {
    api
      .get({ url: '/admin/userInfo', doNotLogout: true })
      .then(
        (response) => dispatch(actions.admin.getUserInfo.fulfilled(response)),
        () => dispatch(actions.admin.getUserInfo.rejected())
      );
  };

  return reduction;
}

function* getUserInfoFulfilled(reduction, response) {
  yield function effect(dispatch) {
    dispatch(actions.admin.getImpersonation());
  };

  const state = INITIAL_STATE
    .set('authorized', true)
    .set('userInfo', fromJS(response));

  return reduction.set('admin', state);
}

function* getImpersonation(reduction) {
  yield function effect(dispatch) {
    api
      .get({ url: '/admin/impersonation', doNotLogout: true })
      .then(
        (response) => dispatch(actions.admin.getImpersonation.fulfilled(response)),
        () => dispatch(actions.admin.getImpersonation.rejected())
      );
  };

  return reduction;
}

function* getImpersonationFulfilled(reduction, impersonation) {
  return reduction.setIn(['admin', 'impersonation'], fromJS(impersonation));
}

function* impersonateUser(reduction, userId) {
  yield function effect(dispatch) {
    api
      .get({ url: `/admin/impersonation/impersonate/${userId}` })
      .then(
        () => dispatch(actions.admin.impersonateUser.fulfilled()),
        (error) => dispatch(actions.admin.impersonateUser.rejected(error))
      );
  };

  return reduction;
}

function* impersonateUserRejected(reduction, error) {
  yield function effect() {
    alert('Error: could not impersonate user');
  };

  console.error(error);
  return reduction;
}

function* stopImpersonating(reduction) {
  yield function effect(dispatch) {
    api
      .get({ url: '/admin/impersonation/cancel' })
      .then(
        () => dispatch(actions.admin.getImpersonation()),
        () => dispatch(actions.admin.stopImpersonating.rejected())
      );
  };

  return reduction;
}

function* getShopCounts(reduction) {
  yield function effect(dispatch) {
    api
      .get({ url: '/admin/shops/counts' })
      .then(
        (response) => dispatch(actions.admin.getShopCounts.fulfilled(response)),
        () => dispatch(actions.admin.getShopCounts.rejected())
      );
  };

  return reduction;
}

function* getShopCountsFulfilled(reduction, shopCounts) {
  return reduction.setIn(['admin', 'shopCounts'], fromJS(shopCounts));
}

function* searchShops(reduction, query) {
  if (!query) {
    return reduction
      .deleteIn(['admin', 'shopsLookup', 'error'])
      .setIn(['admin', 'shopsLookup', 'query'], '')
      .setIn(['admin', 'shopsLookup', 'loading'], false)
      .setIn(['admin', 'shopsLookup', 'result'], EMPTY.LIST);
  }

  yield function effect(dispatch) {
    api
      .get({ url: '/admin/shops/search', params: { query }})
      .then(
        (response) => dispatch(actions.admin.searchShops.fulfilled(response)),
        (error) => dispatch(actions.admin.searchShops.rejected(error))
      );
  };

  return reduction
    .deleteIn(['admin', 'shopsLookup', 'error'])
    .setIn(['admin', 'shopsLookup', 'loading'], true)
    .setIn(['admin', 'shopsLookup', 'query'], query);
}

function* searchShopsFulfilled(reduction, searchResult) {
  return reduction
    .setIn(['admin', 'shopsLookup', 'loading'], false)
    .setIn(['admin', 'shopsLookup', 'result'], fromJS(searchResult));
}

function* searchShopsRejected(reduction, error) {
  return reduction
    .setIn(['admin', 'shopsLookup', 'loading'], false)
    .setIn(['admin', 'shopsLookup', 'error'], error);
}

function* searchUsers(reduction, query) {
  if (!query) {
    return reduction
      .deleteIn(['admin', 'usersLookup', 'error'])
      .setIn(['admin', 'usersLookup', 'query'], '')
      .setIn(['admin', 'usersLookup', 'loading'], false)
      .setIn(['admin', 'usersLookup', 'result'], EMPTY.LIST);
  }

  yield function effect(dispatch) {
    api
      .get({ url: '/admin/users/search', params: { query }})
      .then(
        (response) => dispatch(actions.admin.searchUsers.fulfilled(response)),
        (error) => dispatch(actions.admin.searchUsers.rejected(error))
      );
  };

  return reduction
    .deleteIn(['admin', 'usersLookup', 'error'])
    .setIn(['admin', 'usersLookup', 'loading'], true)
    .setIn(['admin', 'usersLookup', 'query'], query);
}

function* searchUsersFulfilled(reduction, searchResult) {
  return reduction
    .setIn(['admin', 'usersLookup', 'loading'], false)
    .setIn(['admin', 'usersLookup', 'result'], fromJS(searchResult));
}

function* searchUsersRejected(reduction, error) {
  return reduction
    .setIn(['admin', 'usersLookup', 'loading'], false)
    .setIn(['admin', 'usersLookup', 'error'], error);
}

function* getShopDetails(reduction, { shopId, db }) {
  yield function effect(dispatch) {
    Promise.all([
      api.get({ url: `/admin/shops/${shopId}`, params: { db, denorm: true }}),
      api.get({ url: `/admin/shops/${shopId}/owners`, params: { db }}),
      api.get({ url: `/admin/shops/${shopId}/products/search`, params: { db }}),
    ])
      .then(
        ([shop, owners, products]) => dispatch(actions.admin.getShopDetails.fulfilled({ shop, owners, products })),
        (error) => dispatch(actions.admin.getShopDetails.rejected(error))
      );
  };

  return reduction.setIn(['admin', 'shopDetails', 'loading'], true);
}

function* getShopDetailsFulfilled(reduction, { shop, owners, products }) {
  return reduction
    .setIn(['admin', 'shopDetails', 'loading'], false)
    .setIn(['admin', 'shopDetails', 'shop'], fromJS(shop))
    .setIn(['admin', 'shopDetails', 'owners'], fromJS(owners))
    .setIn(['admin', 'shopDetails', 'productsStateCounts'], fromJS(products.stateCounts));
}

function* getShopDetailsRejected(reduction, error) {
  return reduction
    .setIn(['admin', 'shopDetails', 'loading'], false)
    .setIn(['admin', 'shopDetails', 'error'], getErrorString(error));
}

function* getUserDetails(reduction, userId) {
  yield function effect(dispatch) {
    Promise.all([
      api.get({ url: `/admin/users/${userId}` }),
      api.get({ url: `/admin/users/${userId}/shops` }),
    ])
      .then(
        ([user, shops]) => dispatch(actions.admin.getUserDetails.fulfilled({ user, shops })),
        (error) => dispatch(actions.admin.getUserDetails.rejected(error))
      );
  };

  return reduction.setIn(['admin', 'userDetails', 'loading'], true);
}

function* getUserDetailsFulfilled(reduction, { user, shops }) {
  return reduction
    .setIn(['admin', 'userDetails', 'loading'], false)
    .setIn(['admin', 'userDetails', 'shops'], List(shops.map(Map)))
    .setIn(['admin', 'userDetails', 'user'], Map(user));
}

function* getUserDetailsRejected(reduction, error) {
  return reduction
    .setIn(['admin', 'userDetails', 'loading'], false)
    .setIn(['admin', 'userDetails', 'error'], getErrorString(error));
}

function* clearUserDetails(reduction) {
  return reduction.setIn(['admin', 'userDetails'], INITIAL_STATE.get('userDetails'));
}

function* clearShopDetails(reduction) {
  return reduction
    .setIn(['admin', 'shopDetails'], INITIAL_STATE.get('shopDetails'))
    .setIn(['admin', 'reassignShop'], INITIAL_STATE.get('reassignShop'));
}

function* refreshShop(reduction, { shopId, db }) {
  yield function effect(dispatch) {
    api
      .get({ url: `/admin/shops/${shopId}/refresh`, params: { db, userAction: 'adminRefresh' }})
      .then(
        () => dispatch(actions.admin.scheduleShopPoll({ shopId, db })),
        (error) => dispatch(actions.admin.refreshShop.rejected(error))
      );
  };

  return reduction
    .setIn(['admin', 'shopDetails', 'syncStart'], getActualDate().toISOString())
    .setIn(['admin', 'shopDetails', 'refreshInProgress'], true);
}

function* refreshShopRejected(reduction, response) {
  const message = response.status === 400
    ? 'Error: could not duplicate refresh shop'
    : 'Error: could not refresh shop';

  yield function effect() {
    alert(message);
  };

  return reduction.setIn(['admin', 'shopDetails', 'refreshInProgress'], false);
}

function* deleteShop(reduction, { shopId, db }) {
  yield function effect() {
    api
      .delete({ url: `/admin/shops/${shopId}`, params: { db }})
      .then(
        () => goBack(ROUTES.ADMIN.ROOT),
        () => alert('Error: could not sync delete shop')
      );
  };

  return reduction;
}

function* scheduleShopPoll(reduction, { shopId, db }) {
  yield function effect(dispatch) {
    function callback() {
      dispatch(actions.admin.pollShopDetails({ shopId, db }));
    }

    setTimeout(callback, 5000);
  };

  return reduction;
}

function* pollShopDetails(reduction, { shopId, db }) {
  yield function effect(dispatch) {
    function callback(shop) {
      if (
        shop.last_sync_timestamp === DEFAULT_LAST_SYNC_TIMESTAMP ||
        isAfter(new Date(shop.last_sync_timestamp), new Date(reduction.getIn(['admin', 'shopDetails', 'syncStart'])))
      ) {
        dispatch(actions.admin.pollShopDetails.fulfilled(shop));
      } else {
        dispatch(actions.admin.scheduleShopPoll({ shopId, db }));
      }
    }

    api
      .get({ url: `/admin/shops/${shopId}`, params: { db }})
      .then(
        callback,
        () => dispatch(actions.admin.pollShopDetails.rejected())
      );
  };

  return reduction;
}

function* pollShopDetailsFulfilled(reduction, { id: shopId, db }) {
  yield function effect(dispatch) {
    dispatch(actions.admin.getShopDetails({ shopId, db }));
  };

  return reduction
    .deleteIn(['admin', 'shopDetails', 'syncStart'])
    .setIn(['admin', 'shopDetails', 'refreshInProgress'], false);
}

function* pollShopDetailsRejected(reduction) {
  return reduction
    .deleteIn(['admin', 'shopDetails', 'syncStart'])
    .setIn(['admin', 'shopDetails', 'refreshInProgress'], false);
}

function* reassignShopOpenModal(reduction) {
  const state = INITIAL_STATE
    .get('reassignShop')
    .set('modalShown', true);

  return reduction.setIn(['admin', 'reassignShop'], state);
}

function* reassignShopCloseModal(reduction) {
  const state = INITIAL_STATE.get('reassignShop');
  return reduction.setIn(['admin', 'reassignShop'], state);
}

function* searchUsersForReassign(reduction, query) {
  if (!query) {
    return reduction
      .deleteIn(['admin', 'reassignShop', 'error'])
      .setIn(['admin', 'reassignShop', 'query'], '')
      .setIn(['admin', 'reassignShop', 'loading'], false)
      .setIn(['admin', 'reassignShop', 'result'], EMPTY.LIST);
  }

  const db = reduction.getIn(['admin', 'shopDetails', 'shop', 'db']);

  yield function effect(dispatch) {
    api
      .get({ url: '/admin/users/searchSingleDb', params: { db, query }})
      .then(
        (response) => dispatch(actions.admin.searchUsersForReassign.fulfilled(response)),
        (error) => dispatch(actions.admin.searchUsersForReassign.rejected(error))
      );
  };

  return reduction
    .deleteIn(['admin', 'reassignShop', 'error'])
    .setIn(['admin', 'reassignShop', 'loading'], true)
    .setIn(['admin', 'reassignShop', 'query'], query);
}

function* searchUsersForReassignFulfilled(reduction, searchResult) {
  const shop = reduction.getIn(['admin', 'shopDetails', 'shop']);
  const shopCompanyId = shop.getIn(['accountsById', shop.get('account_id'), 'company_id']);
  const result = searchResult.filter(user => user.company_id !== shopCompanyId);

  return reduction
    .setIn(['admin', 'reassignShop', 'loading'], false)
    .setIn(['admin', 'reassignShop', 'result'], fromJS(result));
}

function* searchUsersForReassignRejected(reduction, error) {
  return reduction
    .setIn(['admin', 'reassignShop', 'loading'], false)
    .setIn(['admin', 'reassignShop', 'error'], error);
}

function* reassignShop(reduction, userId) {
  const shop = reduction.getIn(['admin', 'shopDetails', 'shop']);
  const shopId = shop.get('id');
  const db = shop.get('db');

  yield function effect(dispatch) {
    api
      .get({ url: `/admin/shops/${shopId}/reassign/${userId}`, params: { db }})
      .then(
        () => dispatch(actions.admin.reassignShop.fulfilled({ shopId, db })),
        (error) => dispatch(actions.admin.reassignShop.rejected(error))
      );
  };

  return reduction;
}

function* reassignShopFulfilled(reduction, { shopId, db }) {
  yield function effect(dispatch) {
    dispatch(actions.admin.getShopDetails({ shopId, db }));
    dispatch(actions.admin.reassignShopCloseModal());
  };

  return reduction;
}

function* reassignShopRejected(reduction, error) {
  return reduction.setIn(['admin', 'reassignShop', 'error'], getErrorString(error));
}

function* getApplicants(reduction) {
  yield function effect(dispatch) {
    api.affiliates.getApplicants()
      .then(
        (response) => dispatch(actions.admin.getApplicants.fulfilled(response)),
        (error) => dispatch(actions.admin.getApplicants.rejected(error))
      );
  };

  return reduction.setIn(['admin', 'applicantsLookup', 'loading'], true);
}

function* getApplicantsFulfilled(reduction, response) {
  function reduceResponse(result, item) {
    return result.push(Map({
      id: item.id,
      email: item.email,
      affiliateStatus: item.affiliate_status,
      affiliateSurvey: item.affiliate_survey,
    }));
  }

  return reduction
    .setIn(['admin', 'applicantsLookup', 'loading'], false)
    .setIn(['admin', 'applicantsLookup', 'result'], response.applicants.reduce(reduceResponse, EMPTY.LIST));
}

function* getApplicantsRejected(reduction, error) {
  return reduction
    .setIn(['admin', 'applicantsLookup', 'loading'], false)
    .setIn(['admin', 'applicantsLookup', 'error'], error);
}

function* activateApplicant(reduction, userId) {
  yield function effect(dispatch) {
    api.affiliates.activateApplicant(userId)
      .then(
        () => {
          alert('Affiliate activation successful');
          return dispatch(actions.admin.activateApplicant.fulfilled(userId));
        },
        (error) => {
          alert('Affiliate activation failed');
          return dispatch(actions.admin.activateApplicant.rejected({ error, userId }));
        }
      );
  };

  return reduction
    .updateIn(['admin', 'applicantsLookup', 'processing'], EMPTY.SET, function update(items) {
      return items.push(userId);
    });
}

function* activateApplicantFulfilled(reduction, userId) {
  yield function effect(dispatch) {
    dispatch(actions.admin.getApplicants());
  };

  return reduction
    .updateIn(['admin', 'applicantsLookup', 'processing'], EMPTY.SET, function update(items) {
      return items.filter(item => item !== userId);
    });
}

function* activateApplicantRejected(reduction, { error, userId }) {
  yield function effect() {
    alert('Error: could not activate referral');
  };

  console.error(error);
  return reduction
    .updateIn(['admin', 'applicantsLookup', 'processing'], EMPTY.SET, function update(items) {
      return items.filter(item => item !== userId);
    });
}

function* rejectApplicant(reduction, userId) {
  yield function effect(dispatch) {
    api.affiliates.rejectApplicant(userId)
      .then(
        () => {
          alert('Affiliate rejection successful');
          return dispatch(actions.admin.rejectApplicant.fulfilled(userId));
        },
        (error) => {
          alert('Affiliate rejection failed');
          return dispatch(actions.admin.rejectApplicant.rejected({ error, userId }));
        }
      );
  };

  return reduction
    .updateIn(['admin', 'applicantsLookup', 'processing'], EMPTY.SET, function update(items) {
      return items.push(userId);
    });
}

function* rejectApplicantFulfilled(reduction, userId) {
  yield function effect(dispatch) {
    dispatch(actions.admin.getApplicants());
  };

  return reduction
    .updateIn(['admin', 'applicantsLookup', 'processing'], EMPTY.SET, function update(items) {
      return items.filter(item => item !== userId);
    });
}

function* rejectApplicantRejected(reduction, { error, userId }) {
  yield function effect() {
    alert('Error: could not reject referral');
  };

  console.error(error);
  return reduction
    .updateIn(['admin', 'applicantsLookup', 'processing'], EMPTY.SET, function update(items) {
      return items.filter(item => item !== userId);
    });
}

function* getPayouts(reduction) {
  yield function effect(dispatch) {
    api.affiliates.getPayouts()
      .then(
        (response) => dispatch(actions.admin.getPayouts.fulfilled(response)),
        (error) => dispatch(actions.admin.getPayouts.rejected(error))
      );
  };

  return reduction.setIn(['admin', 'payoutsLookup', 'loading'], true);
}

function* getPayoutsFulfilled(reduction, response) {
  function reduceResponse(result, item) {
    return result.push(Map({
      id: item.id,
      userId: item.referrer_user_id,
      email: item.referrer_email,
      milestone: addThousandsSeparator(item.milestone),
      totalPayout: `$${addThousandsSeparator(item.total_payout_amount / 100, 2, true)}`,
    }));
  }

  return reduction
    .setIn(['admin', 'payoutsLookup', 'loading'], false)
    .setIn(['admin', 'payoutsLookup', 'result'], response.data.reduce(reduceResponse, EMPTY.LIST));
}

function* getPayoutsRejected(reduction, error) {
  return reduction
    .setIn(['admin', 'payoutsLookup', 'loading'], false)
    .setIn(['admin', 'payoutsLookup', 'error'], error);
}

function* getPayoutsDetails(reduction, { payoutId }) {
  yield function effect(dispatch) {
    api.affiliates.getPayoutsDetails(payoutId)
      .then(
        (response) => dispatch(actions.admin.getPayoutsDetails.fulfilled(response)),
        (error) => dispatch(actions.admin.getPayoutsDetails.rejected(error))
      );
  };

  return reduction.setIn(['admin', 'payoutsDetails', 'loading'], true);
}

function* getPayoutsDetailsFulfilled(reduction, response) {
  const item = response.data;
  const payout = Map({
    id: item.id,
    referrerId: item.referrer_user_id,
    referrerEmail: item.referrer_email,
    milestone: addThousandsSeparator(item.milestone),
    convertedAmount: addThousandsSeparator(item.converted_amount / 100, 2, true),
    bonus: addThousandsSeparator(item.bonus / 100, 2, true),
    payoutAmount: addThousandsSeparator(item.total_payout_amount / 100, 2, true),
    payTo: item.pay_to,
    paymentMethod: item.payment_method,
    status: item.status,
    referees: List(item.referees.map(referee => Map({
      id: referee.user_id,
      email: referee.email,
      amount: addThousandsSeparator(referee.amount / 100, 2, true),
      milestone: addThousandsSeparator(referee.milestone),
      subscribedAt: referee.subscribed_at,
      shopId: referee.shop_id,
      db: referee.db,
    }))),
  });

  return reduction
    .setIn(['admin', 'payoutsDetails', 'loading'], false)
    .setIn(['admin', 'payoutsDetails', 'payout'], payout);
}

function* getPayoutsDetailsRejected(reduction, error) {
  return reduction
    .setIn(['admin', 'payoutsDetails', 'loading'], false)
    .setIn(['admin', 'payoutsDetails', 'error'], error);
}

function* clearPayoutsDetails(reduction) {
  return reduction.setIn(['admin', 'payoutsDetails'], INITIAL_STATE.get('payoutsDetails'));
}

function* markPayoutAsPaid(reduction, { payoutId }) {
  yield function effect(dispatch) {
    api.affiliates.markPayoutAsPaid(payoutId)
      .then(
        () => dispatch(actions.admin.markPayoutAsPaid.fulfilled(payoutId)),
        (error) => dispatch(actions.admin.markPayoutAsPaid.rejected(error))
      );
  };

  return reduction.setIn(['admin', 'payoutsDetails', 'inProgress'], true);
}

function* markPayoutAsPaidFulfilled(reduction, payoutId) {
  yield function effect(dispatch) {
    dispatch(actions.admin.getPayoutsDetails({ payoutId }));
  };

  return reduction
    .setIn(['admin', 'payoutsDetails', 'inProgress'], false);
}

function* markPayoutAsPaidRejected(reduction, error) {
  return reduction
    .setIn(['admin', 'payoutsDetails', 'inProgress'], false)
    .setIn(['admin', 'payoutsDetails', 'error'], error);
}

function* getReferrals(reduction) {
  yield function effect(dispatch) {
    api.affiliates.getReferrals()
      .then(
        (response) => dispatch(actions.admin.getReferrals.fulfilled(response)),
        (error) => dispatch(actions.admin.getReferrals.rejected(error))
      );
  };

  return reduction.setIn(['admin', 'referralsLookup', 'loading'], true);
}

function* getReferralsFulfilled(reduction, response) {
  function reduceResponse(result, item) {
    return result.push(Map({
      userId: item.referrer_user_id,
      email: item.email,
      refereesCount: item.refereescount,
    }));
  }

  return reduction
    .setIn(['admin', 'referralsLookup', 'loading'], false)
    .setIn(['admin', 'referralsLookup', 'result'], response.reduce(reduceResponse, EMPTY.LIST));
}

function* getReferralsRejected(reduction, error) {
  return reduction
    .setIn(['admin', 'referralsLookup', 'loading'], false)
    .setIn(['admin', 'referralsLookup', 'error'], error);
}

function* getReferralsDetails(reduction, { referralId }) {
  yield function effect(dispatch) {
    api.affiliates.getReferralsDetails(referralId)
      .then(
        (response) => dispatch(actions.admin.getReferralsDetails.fulfilled({ response, referralId } )),
        (error) => dispatch(actions.admin.getReferralsDetails.rejected(error))
      );
  };

  return reduction.setIn(['admin', 'referralsDetails', 'loading'], true);
}

function* getReferralsDetailsFulfilled(reduction, { response, referralId }) {
  const count = response.count;
  const details = response.details;

  function reduceDetails(result, item) {
    return result.push(Map(item));
  }

  return reduction
    .setIn(['admin', 'referralsDetails', 'loading'], false)
    .setIn(['admin', 'referralsDetails', 'referral'],
      Map({
        count,
        referralId,
        details: details.reduce(reduceDetails, EMPTY.LIST),
      })
    );
}

function* getReferralsDetailsRejected(reduction, error) {
  return reduction
    .setIn(['admin', 'referralsDetails', 'loading'], false)
    .setIn(['admin', 'referralsDetails', 'error'], error);
}

function* clearReferralsDetails(reduction) {
  return reduction.setIn(['admin', 'referralsDetails'], INITIAL_STATE.get('referralsDetails'));
}

const admin = {
  [actions.admin.getUserInfo]: getUserInfo,
  [actions.admin.getUserInfo.fulfilled]: getUserInfoFulfilled,
  [actions.admin.getUserInfo.rejected]: setUnauthorized,
  [actions.admin.getImpersonation]: getImpersonation,
  [actions.admin.getImpersonation.fulfilled]: getImpersonationFulfilled,
  [actions.admin.getImpersonation.rejected]: setUnauthorized,
  [actions.admin.stopImpersonating]: stopImpersonating,
  [actions.admin.stopImpersonating.rejected]: setUnauthorized,
  [actions.admin.getShopCounts]: getShopCounts,
  [actions.admin.getShopCounts.fulfilled]: getShopCountsFulfilled,
  [actions.admin.getShopCounts.rejected]: setUnauthorized,
  [actions.admin.searchShops]: searchShops,
  [actions.admin.searchShops.fulfilled]: searchShopsFulfilled,
  [actions.admin.searchShops.rejected]: searchShopsRejected,
  [actions.admin.searchUsers]: searchUsers,
  [actions.admin.searchUsers.fulfilled]: searchUsersFulfilled,
  [actions.admin.searchUsers.rejected]: searchUsersRejected,
  [actions.admin.impersonateUser]: impersonateUser,
  [actions.admin.impersonateUser.fulfilled]: getImpersonation,
  [actions.admin.impersonateUser.rejected]: impersonateUserRejected,
  [actions.admin.getShopDetails]: getShopDetails,
  [actions.admin.getShopDetails.fulfilled]: getShopDetailsFulfilled,
  [actions.admin.getShopDetails.rejected]: getShopDetailsRejected,
  [actions.admin.getUserDetails]: getUserDetails,
  [actions.admin.getUserDetails.fulfilled]: getUserDetailsFulfilled,
  [actions.admin.getUserDetails.rejected]: getUserDetailsRejected,
  [actions.admin.clearShopDetails]: clearShopDetails,
  [actions.admin.clearUserDetails]: clearUserDetails,
  [actions.admin.refreshShop]: refreshShop,
  [actions.admin.refreshShop.rejected]: refreshShopRejected,
  [actions.admin.deleteShop]: deleteShop,
  [actions.admin.scheduleShopPoll]: scheduleShopPoll,
  [actions.admin.pollShopDetails]: pollShopDetails,
  [actions.admin.pollShopDetails.fulfilled]: pollShopDetailsFulfilled,
  [actions.admin.pollShopDetails.rejected]: pollShopDetailsRejected,
  [actions.admin.reassignShopOpenModal]: reassignShopOpenModal,
  [actions.admin.reassignShopCloseModal]: reassignShopCloseModal,
  [actions.admin.searchUsersForReassign]: searchUsersForReassign,
  [actions.admin.searchUsersForReassign.fulfilled]: searchUsersForReassignFulfilled,
  [actions.admin.searchUsersForReassign.rejected]: searchUsersForReassignRejected,
  [actions.admin.reassignShop]: reassignShop,
  [actions.admin.reassignShop.fulfilled]: reassignShopFulfilled,
  [actions.admin.reassignShop.rejected]: reassignShopRejected,
  [actions.admin.getApplicants]: getApplicants,
  [actions.admin.getApplicants.fulfilled]: getApplicantsFulfilled,
  [actions.admin.getApplicants.rejected]: getApplicantsRejected,
  [actions.admin.activateApplicant]: activateApplicant,
  [actions.admin.activateApplicant.fulfilled]: activateApplicantFulfilled,
  [actions.admin.activateApplicant.rejected]: activateApplicantRejected,
  [actions.admin.rejectApplicant]: rejectApplicant,
  [actions.admin.rejectApplicant.fulfilled]: rejectApplicantFulfilled,
  [actions.admin.rejectApplicant.rejected]: rejectApplicantRejected,
  [actions.admin.getPayouts]: getPayouts,
  [actions.admin.getPayouts.fulfilled]: getPayoutsFulfilled,
  [actions.admin.getPayouts.rejected]: getPayoutsRejected,
  [actions.admin.getPayoutsDetails]: getPayoutsDetails,
  [actions.admin.getPayoutsDetails.fulfilled]: getPayoutsDetailsFulfilled,
  [actions.admin.getPayoutsDetails.rejected]: getPayoutsDetailsRejected,
  [actions.admin.clearPayoutsDetails]: clearPayoutsDetails,
  [actions.admin.markPayoutAsPaid]: markPayoutAsPaid,
  [actions.admin.markPayoutAsPaid.fulfilled]: markPayoutAsPaidFulfilled,
  [actions.admin.markPayoutAsPaid.rejected]: markPayoutAsPaidRejected,
  [actions.admin.getReferrals]: getReferrals,
  [actions.admin.getReferrals.fulfilled]: getReferralsFulfilled,
  [actions.admin.getReferrals.rejected]: getReferralsRejected,
  [actions.admin.getReferralsDetails]: getReferralsDetails,
  [actions.admin.getReferralsDetails.fulfilled]: getReferralsDetailsFulfilled,
  [actions.admin.getReferralsDetails.rejected]: getReferralsDetailsRejected,
  [actions.admin.clearReferralsDetails]: clearReferralsDetails,
};

export default admin;
